import { EnrollmentDetailDataResponse } from '../enrollment/enrollment-steps-service-types'
import { AuthorizationStatusType } from './authorization-service-utils'

// ==================== ENUMS ====================

export enum AuthorizationStatusEnum {
  PENDING = -1,
  LEADER_REJECTED = 0,
  WAITING_ENROLLMENT = 1,
  ERROR_ENROLLMENT = 2,
  ONGOING = 3,
  ABANDONED = 4,
  FINISHED = 5,
  EVALUATED = 6, // DEPRECATED
  WAITING_PAYMENT = 7, // DEPRECATED
  ERROR_PAYMENT = 8, // DEPRECATED
  EXPIRED = 9,
  WAITING_REFUND = 10,
  WAITING_BOLETO = 11, // DEPRECATED
  LEADER_APPROVED = 12,
  SENT_BOLETO = 13, // DEPRECATED
  SENT_NF = 14,
  PROCESSING_REFUND = 15,
  WAITING_SECOND_APPROVER = 16,
  WAITING_BUDGET_APPROVER = 17,
  WAITING_INSTITUTION_PAYMENT = 18,
  WAITING_FINANCIAL_PASS_PAYMENT = 19
}

export enum CourseType {
  PRIVATE_LESSON = 'privateLesson',
  PARTNER = 'partner',
  COURSE = 'course',
  EVENT = 'event',
  BOOK = 'book',
  MENTORING = 'mentoring',
  OTHER = 'other'
}

export enum RequestType {
  MARKETPLACE = 'false',
  REFUND = 'true'
}

export enum AccountType {
  COLLABORATOR = 'COLLABORATOR',
  DEPENDENT = 'DEPENDENT'
}

export enum ComplianceType {
  MISSING_NF = 'missingNF',
  MISSING_PAYMENT = 'missingPayment'
}

// ==================== DOMAIN MODELS ====================

export interface AuthorizationStatus {
  id: number
  code: string
  name: string
  active: boolean
}

export interface AuthorizationStatusGroup {
  id: number
  name: string
  displayName: string
  statusIds?: number[]
  visible: boolean
  visibility?: boolean
  messageKey?: string
}

export interface AuthorizationLabel {
  id: number
  name: string
  backgroundColor: string
  textColor: string
  color: string
}

export interface AuthorizationLabelRelation {
  id: number
  authorizationId: number
  labelId: number
  label: AuthorizationLabel
}

export interface BankInfo {
  bankCode: string
  bankName: string
  agency: string
  account: string
  accountType: string
}

export interface TedInfo extends BankInfo {
  cpfCnpj: string
  name: string
}

export interface PixInfo {
  pixKey: string
  pixKeyType: string
}

export interface CardInfo {
  lastDigits: string
  brand: string
  holderName: string
}

export interface CourseSkill {
  id: number
  name: string
  userId: number
  courseId: number
}

// ==================== FILTER & SORT ====================

export interface FilterOptions {
  companies: Array<{ id: number; name: string }>
  departments: Array<{ id: number; name: string }>
  jobs: Array<{ id: number; name: string }>
  status: AuthorizationStatus[]
  partners: Array<{ id: number; name: string }>
  labels: AuthorizationLabel[]
  accountTypes: string[]
}

export interface SortOption {
  value: string
  label: string
}

export interface AuthorizationFilterParams {
  q?: string
  page?: number
  pageSize?: number
  startDate?: string
  endDate?: string
  createdStartDate?: string
  createdEndDate?: string
  companies?: string
  departments?: string
  jobs?: string
  status?: string
  compliance?: ComplianceType
  partners?: string
  priceMin?: number
  priceMax?: number
  requestTypes?: RequestType
  courseTypes?: CourseType
  freeCourseFlowDisable?: boolean
  onlyPass?: boolean
  userTests?: boolean
  companyTests?: boolean
  labels?: string
  accountType?: AccountType
  orderBy?: string
  currentSection?: string
  authFromEmail?: number
}

// ==================== AUTHORIZATION ENTITIES ====================

export interface AuthorizationItem {
  // Identity
  id: number
  authorizationId: number

  // User Info
  userId: number
  userName: string
  userSocialName?: string
  userEmail: string
  userPhoto?: string
  userDocument: string
  userDeactivated: boolean
  accountType: AccountType
  userAccountType?: string
  isDependent: boolean
  userTestAccount: boolean

  // Company Info
  companyId: number
  companyName: string
  departmentId?: number
  departmentName?: string
  jobId?: number
  jobName?: string
  companyTestAccount: boolean

  // Course Info
  courseId: number
  courseName: string
  name: string
  subtitle?: string
  courseType: CourseType
  contentType: string
  courseLink?: string
  link?: string
  duration?: string
  flowType?: number
  productId?: number
  subtypeId?: number

  // Institution Info
  institutionId: number
  institutionName: string
  institution?: string
  institutionLogo?: string
  institutionPaymentDeadline?: string
  paymentDeadline?: string

  // Location
  city?: string
  state?: string
  country?: string

  // Pricing
  price: number
  value?: number
  originalPrice?: number
  userPaidAmount: number
  userPaid?: number
  creditsPaid: number
  coParticipationPercentage: number
  coParticipation?: number

  // Status
  statusId: number
  status?: number
  statusCode: string
  statusName: string
  currentStatus?: AuthorizationStatusType

  // Payment
  paymentType: string
  paymentTypeId?: number
  transactionId?: string
  walletId?: number
  expiresAt?: string
  sentNf: boolean
  nfUrl?: string

  // Dates
  createdAt: string
  updatedAt: string
  startDate?: string
  endDate?: string

  // Approvals
  requiresLeaderApproval: boolean
  leaderInChargeName?: string
  hadLeaderApproval: boolean
  requiresSecondApprover: boolean
  requiresBudgetApproval: boolean

  // Additional
  isRefund: boolean
  isSkillPass: boolean
  labels?: AuthorizationLabel[]
  programId?: number
  programName?: string
  type?: string
}

export interface AuthorizationHistoryItem {
  id: number
  authorizationId: number
  userId: number
  userName: string
  userPhoto?: string
  comment: string
  statusId: number
  statusName: string
  isVisible: boolean
  isNf: boolean
  attachmentUrl?: string
  createdAt: string
  rating?: number
  tag?: string
}

// ==================== API RESPONSES ====================

export interface AuthorizationPaginatedResponse {
  results: AuthorizationItem[]
  pages: number
  pageSize: number
  resultsQuantity: number
}

export interface AuthorizationHistoryResponse {
  authorizationHistoryList: AuthorizationHistoryItem[]
  authorizationInfo: AuthorizationDetail
  rating?: number
  payableAuthorization: boolean
}

export interface AuthorizationDetail extends AuthorizationItem, AuthorizationHistoryResponse {
  balance: number
  companyRequiresLeaderPayment: boolean
  skills?: CourseSkill[]
  rating?: number
  daysUntilEvaluation?: number
  bankInfo?: BankInfo
  tedInfo?: TedInfo
  pixInfo?: PixInfo
  payable: boolean
  authorizationStatusEmailResendable?: boolean
  externCourse?: boolean
  card?: CardInfo
  referenceNumber?: string
  travelType?: string
  oneWayFlightDate?: string
  returnFlightDate?: string
  accommodationCheckInDate?: string
  accommodationCheckOutDate?: string
  pix?: string
  tedName?: string
  tedCpf?: string
  bankCode?: string
  bankName?: string
  bankAgency?: string
  bankAccount?: string

  // SkillPass Payment Fields
  passPaymentValue?: number
  passPaymentDate?: string
  passPaymentStatus?: string
  passPaymentScheduled?: boolean
  passPaymentProofUrl?: string
  passPaymentInvoiceUrl?: string
  passPaymentNftUrls?: string[]
  institutionPaymentProofUrl?: string

  // Compliance Fields
  nfDeadline?: string
  nfComments?: string
  paymentScheduledDate?: string
  paymentProofUrl?: string
  paymentComments?: string
  trainingPaymentDate?: string
}

export interface AuthorizationPageLoaderData {
  authorizationDetail: AuthorizationDetail
  availableLabels: AuthorizationLabel[]
  requestDetails: EnrollmentDetailDataResponse
}

// ==================== ACTION PAYLOADS ====================

export interface ApproveRejectPayload {
  id: number
  comment?: string
  submitFromEmail?: boolean
  file?: File
}

export interface EnrollmentAcceptedPayload {
  id: number
  comment?: string
  updatedValue?: number
  manualRefund?: boolean
  file?: File
}

export interface SuperadminRejectedPayload {
  id: number
  comment?: string
  cancelReason?: string
  file?: File
}

export interface ChangeStatusPayload {
  id: number
  statusId: number
  comment: string
}

export interface AddCommentPayload {
  id: number
  comment: string
  is_visible: boolean
  is_nf?: boolean
  file?: File
}

export interface UpdateLabelsPayload {
  id: number
  labelsId?: number[]
}

export interface InstitutionPaymentDefaultPayload {
  id: number
  enrollment: string
}

export interface InstitutionPaymentTransferPayload {
  id: number
  enrollment: string
  institutionTransfer: {
    bankCode: string
    agency: string
    account: string
    accountType: string
    cpfCnpj: string
    name: string
  }
}

export interface InstitutionPaymentBoletoPayload {
  id: number
  enrollment: string
  barcode: string
  dueDate: string
}

export interface InstitutionPaymentAttachmentPayload {
  id: number
  paymentDate: string
  file: File
}

export interface SchedulePassPaymentPayload {
  id: number
  paymentDate: string
}

export interface ScheduleTrainingPaymentPayload {
  id: number
  paymentDate: string
  comment?: string
}

export interface SendEmailPayload {
  authId: number
  emailType: 'WRONG_NF' | 'INVALID_COURSE_CERTIFICATE' | 'VALID_COURSE_CERTIFICATE'
  customMessage?: string
}

export interface CancelRefundPayload {
  authId: number
  comment: string
}

// ==================== CONSTANTS ====================

export const SORT_OPTIONS_DEFAULT: SortOption[] = [
  { value: 'default', label: 'Data de atualização (mais nova)' },
  { value: 'updateDate_asc', label: 'Data de atualização (mais antiga)' },
  { value: 'startDate_desc', label: 'Data de criação (mais nova)' },
  { value: 'startDate_asc', label: 'Data de criação (mais antiga)' },
  { value: 'price_desc', label: 'Maior valor' },
  { value: 'price_asc', label: 'Menor valor' },
  { value: 'institution_asc', label: 'Provedor' }
]

export const SORT_OPTIONS_SUPERADMIN: SortOption[] = [
  ...SORT_OPTIONS_DEFAULT,
  { value: 'company_asc', label: 'Empresa' },
  { value: 'institution_payment_deadline_desc', label: 'Data limite de pagamento (mais nova)' },
  { value: 'institution_payment_deadline_asc', label: 'Data limite de pagamento (mais antiga)' }
]

export const REQUEST_TYPE_OPTIONS = [
  { value: RequestType.MARKETPLACE, label: 'Marketplace' },
  { value: RequestType.REFUND, label: 'Reembolso' }
]

export const COURSE_TYPE_OPTIONS = [
  { value: CourseType.PRIVATE_LESSON, label: 'Aula Particular' },
  { value: CourseType.PARTNER, label: 'Parceiro' },
  { value: CourseType.COURSE, label: 'Curso' },
  { value: CourseType.EVENT, label: 'Evento' },
  { value: CourseType.BOOK, label: 'Livro' },
  { value: CourseType.MENTORING, label: 'Mentoria' },
  { value: CourseType.OTHER, label: 'Outro' }
]

export const COMPLIANCE_OPTIONS = [
  { value: ComplianceType.MISSING_NF, label: 'NF Ausente' },
  { value: ComplianceType.MISSING_PAYMENT, label: 'Pagamento Ausente' }
]

export const BOOLEAN_OPTIONS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' }
]
